import { useQuery } from "@apollo/client";
import { Issue } from "generated-graphql/graphql";

import { VALIDATE_SUBMISSION_WASTE_ACTIVITY_INPUT } from "./steps/useWasteActivityCodeValidation";

/** The saved code lists this hook validates (from `Submission.wasteActivity`). */
type SavedWasteActivityCodes = {
  federalWasteCodes: string[];
  stateWasteCodes: string[];
} | null;

/**
 * Compliance-engine issues for a submission's SAVED Waste Activity waste codes.
 *
 * The in-step hook (`useWasteActivityCodeValidation`) validates in-progress
 * edits to surface inline field errors; this validates the PERSISTED row so the
 * overview cards and the step nav can derive the Waste Activity step's status.
 * That makes the step behave like the facility-scoped steps: a saved-but-flagged
 * step reads as "Invalid" (needs attention) rather than "Complete", instead of
 * turning green the moment it's saved regardless of issues.
 *
 * Returns `[]` until the step has been saved (`wasteActivity` is null). Only the
 * chosen codes drive validation; the recommendation fields just satisfy the
 * shared upsert-input shape.
 */
export function useSubmissionWasteActivityIssues(
  submissionId: string,
  wasteActivity: SavedWasteActivityCodes
): Issue[] {
  const { data } = useQuery(VALIDATE_SUBMISSION_WASTE_ACTIVITY_INPUT, {
    variables: {
      data: {
        submissionId,
        federalWasteCodes: wasteActivity?.federalWasteCodes ?? [],
        stateWasteCodes: wasteActivity?.stateWasteCodes ?? [],
        recommendedFederalWasteCodes: [],
        recommendedStateWasteCodes: [],
      },
    },
    // Nothing to validate until the step has been saved.
    skip: !wasteActivity,
    fetchPolicy: "cache-and-network",
  });

  return wasteActivity ? (data?.submissionWasteActivityInputValidator ?? []) : [];
}
